import { Routes, Route, useLocation } from 'react-router-dom';
import { LoggedNavigate } from '@/components/layout/LoggedNavigate';
import { AnimatePresence } from 'framer-motion';
import { PageTransition } from './PageTransition';
import { lazy, useEffect } from 'react';
import { stripHtmlSuffix } from '@/lib/i18n/routes';
import { LazyBoundary } from '@/components/util/LazyBoundary';
import { lazyRetry } from '@/lib/lazyRetry';
import { usePageViewTracking } from '@/hooks/usePageViewTracking';



// Eagerly load Home page for LCP
import Home from '@/pages/Home';

// Lazy load all other pages for code splitting (with chunk-retry backoff)
const PMSPage = lazy(lazyRetry(() => import('@/pages/product/PMSPage')));
const ChannelManagerPage = lazy(lazyRetry(() => import('@/pages/product/ChannelManagerPage')));
const WebsiteBuilderPage = lazy(lazyRetry(() => import('@/pages/product/WebsiteBuilderPage')));
const FeaturesPage = lazy(lazyRetry(() => import('@/pages/FeaturesPage')));
const BriefDescriptionPage = lazy(lazyRetry(() => import('@/pages/BriefDescriptionPage')));
const AIPage = lazy(lazyRetry(() => import('@/pages/AIPage')));
const VacationRentalPMSPage = lazy(lazyRetry(() => import('@/pages/VacationRentalPMSPage')));
const BestVacationRentalSoftwarePage = lazy(lazyRetry(() => import('@/pages/compare/BestVacationRentalSoftwarePage')));
const SmallBusinessPage = lazy(lazyRetry(() => import('@/pages/for-whom/SmallBusinessPage')));
const MediumLargePage = lazy(lazyRetry(() => import('@/pages/for-whom/MediumLargePage')));
const IntegrationsPage = lazy(lazyRetry(() => import('@/pages/IntegrationsPage')));
const TestimonialsPage = lazy(lazyRetry(() => import('@/pages/TestimonialsPage')));
const PricingPage = lazy(lazyRetry(() => import('@/pages/PricingPage')));
const ComparePage = lazy(lazyRetry(() => import('@/pages/ComparePage')));
const AboutPage = lazy(lazyRetry(() => import('@/pages/AboutPage')));
const ContactPage = lazy(lazyRetry(() => import('@/pages/ContactPage')));
const BlogPage = lazy(lazyRetry(() => import('@/pages/BlogPage')));
const BlogPostPage = lazy(lazyRetry(() => import('@/pages/BlogPostPage')));
const FAQPage = lazy(lazyRetry(() => import('@/pages/FAQPage')));
const TermsPage = lazy(lazyRetry(() => import('@/pages/TermsPage')));
const PublicationTermsPage = lazy(lazyRetry(() => import('@/pages/PublicationTermsPage')));
const PrivacyPage = lazy(lazyRetry(() => import('@/pages/PrivacyPage')));
const WebsiteDevelopmentTermsPage = lazy(lazyRetry(() => import('@/pages/WebsiteDevelopmentTermsPage')));
const NotFound = lazy(lazyRetry(() => import('@/pages/NotFound')));
const AdminPage = lazy(lazyRetry(() => import('@/pages/AdminPage')));
const AuthPage = lazy(lazyRetry(() => import('@/pages/AuthPage')));
const BlogEditorPage = lazy(lazyRetry(() => import('@/pages/admin/BlogEditorPage')));
const BlogPostEditor = lazy(lazyRetry(() => import('@/pages/admin/BlogPostEditor')));
const TestimonialsEditor = lazy(lazyRetry(() => import('@/pages/admin/TestimonialsEditor')));
const UserManagementPage = lazy(lazyRetry(() => import('@/pages/admin/UserManagementPage')));
const EmailSettingsPage = lazy(lazyRetry(() => import('@/pages/admin/EmailSettingsPage')));
const PhotoAlbumPage = lazy(lazyRetry(() => import('@/pages/admin/PhotoAlbumPage')));
const SeoMetaEditor = lazy(lazyRetry(() => import('@/pages/admin/SeoMetaEditor')));
const DnsCheckPage = lazy(lazyRetry(() => import('@/pages/admin/DnsCheckPage')));
const SitemapCheckPage = lazy(lazyRetry(() => import('@/pages/admin/SitemapCheckPage')));
const SeoPublishStatusPage = lazy(lazyRetry(() => import('@/pages/admin/SeoPublishStatusPage')));
const UrlMonitorPage = lazy(lazyRetry(() => import('@/pages/admin/UrlMonitorPage')));
const GscInspectionsPage = lazy(lazyRetry(() => import('@/pages/admin/GscInspectionsPage')));
const GscMonitorPage = lazy(lazyRetry(() => import('@/pages/admin/GscMonitorPage')));
const SeoValidationPage = lazy(lazyRetry(() => import('@/pages/admin/SeoValidationPage')));
const UxMetricsPage = lazy(lazyRetry(() => import('@/pages/admin/UxMetricsPage')));

const RedirectCheckPage = lazy(lazyRetry(() => import('@/pages/admin/RedirectCheckPage')));
const ResetPasswordPage = lazy(lazyRetry(() => import('@/pages/ResetPasswordPage')));
const AppOwnersPrivacyPage = lazy(lazyRetry(() => import('@/pages/legal/AppOwnersPrivacyPage')));
const AppMaintenancePrivacyPage = lazy(lazyRetry(() => import('@/pages/legal/AppMaintenancePrivacyPage')));
const AppPropertyManagerPrivacyPage = lazy(lazyRetry(() => import('@/pages/legal/AppPropertyManagerPrivacyPage')));

// Check if we're in a preview environment (not production)
const isPreviewEnvironment = () => {
  const hostname = window.location.hostname;
  return (
    hostname.includes('localhost') ||
    hostname.includes('preview--') ||
    hostname.includes('-preview') ||
    hostname.includes('lovableproject.com')
  );
};

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Wrap component with LazyBoundary (Suspense + chunk-error recovery) and PageTransition
const withPage = (Component: React.ComponentType) => (
  <LazyBoundary suspenseFallback={<PageLoader />}>
    <PageTransition><Component /></PageTransition>
  </LazyBoundary>
);


// Home page (eagerly loaded)
const HomePage = <PageTransition skipInitialAnimation><Home /></PageTransition>;

export function AnimatedRoutes() {
  const location = useLocation();
  const routeLocation = location.pathname.endsWith('.html')
    ? { ...location, pathname: stripHtmlSuffix(location.pathname) }
    : location;

  usePageViewTracking();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);


  return (
    <AnimatePresence mode="wait">
      <Routes location={routeLocation} key={routeLocation.pathname}>
        {/* ===== ENGLISH ROUTES (no prefix - default) ===== */}
        <Route path="/" element={HomePage} />
        <Route path="/pricing" element={withPage(PricingPage)} />
        <Route path="/compare" element={withPage(ComparePage)} />
        <Route path="/features" element={withPage(FeaturesPage)} />
        <Route path="/what-is-irent" element={withPage(BriefDescriptionPage)} />
        <Route path="/contact" element={withPage(ContactPage)} />
        <Route path="/about" element={withPage(AboutPage)} />
        <Route path="/testimonials" element={withPage(TestimonialsPage)} />
        <Route path="/integrations" element={withPage(IntegrationsPage)} />
        <Route path="/product/holiday-rental-software" element={withPage(PMSPage)} />
        <Route path="/product/channel-manager-holiday-rentals" element={withPage(ChannelManagerPage)} />
        <Route path="/product/website-builder" element={withPage(WebsiteBuilderPage)} />
        <Route path="/solutions/small-business" element={withPage(SmallBusinessPage)} />
        <Route path="/solutions/medium-large" element={withPage(MediumLargePage)} />
        <Route path="/blog" element={withPage(BlogPage)} />
        <Route path="/blog/:slug" element={withPage(BlogPostPage)} />
        <Route path="/faq" element={withPage(FAQPage)} />
        <Route path="/terms" element={withPage(TermsPage)} />
        <Route path="/privacy" element={withPage(PrivacyPage)} />
        <Route path="/publication-terms" element={withPage(PublicationTermsPage)} />
        <Route path="/website-development-terms" element={withPage(WebsiteDevelopmentTermsPage)} />
        <Route path="/ai" element={withPage(AIPage)} />
        <Route path="/holiday-rental-pms" element={withPage(VacationRentalPMSPage)} />
        <Route path="/nl/vakantieverhuur-pms" element={withPage(VacationRentalPMSPage)} />
        <Route path="/es/pms-alquiler-vacacional" element={withPage(VacationRentalPMSPage)} />
        <Route path="/de/ferienvermietung-pms" element={withPage(VacationRentalPMSPage)} />
        <Route path="/fr/pms-location-vacances" element={withPage(VacationRentalPMSPage)} />
        <Route path="/it/pms-affitti-brevi" element={withPage(VacationRentalPMSPage)} />
        <Route path="/pt/pms-alojamento-local" element={withPage(VacationRentalPMSPage)} />
        <Route path="/dk/feriebolig-pms" element={withPage(VacationRentalPMSPage)} />
        {/* Legacy English /vacation-rental-pms slug — intentionally NOT registered as a Route.
            On Lovable static hosting a client-side <Navigate> serves index.html with a status 200
            and the sitewide canonical="/" before JS runs, which causes crawlers to index these
            paths as duplicates of the homepage. Falling through to the 404 route is the safer SEO
            signal. The static /public/{lang}/vacation-rental-pms.html redirect files handle
            any legacy inbound links with a proper canonical + noindex + meta refresh. */}
        <Route path="/compare/best-holiday-rental-software" element={withPage(BestVacationRentalSoftwarePage)} />
        <Route path="/nl/vergelijken/beste-vakantieverhuur-software" element={withPage(BestVacationRentalSoftwarePage)} />
        <Route path="/es/comparar/mejor-software-alquiler-vacacional" element={withPage(BestVacationRentalSoftwarePage)} />
        <Route path="/de/vergleichen/beste-ferienvermietung-software" element={withPage(BestVacationRentalSoftwarePage)} />
        <Route path="/fr/comparer/meilleur-logiciel-location-vacances" element={withPage(BestVacationRentalSoftwarePage)} />
        <Route path="/it/confronto/miglior-software-affitti-vacanze" element={withPage(BestVacationRentalSoftwarePage)} />
        <Route path="/pt/comparar/melhor-software-alojamento-local" element={withPage(BestVacationRentalSoftwarePage)} />
        <Route path="/dk/sammenlign/bedste-feriebolig-software" element={withPage(BestVacationRentalSoftwarePage)} />

        {/* Legacy indexed EN slug → new EN slug. Sidecar public/compare/best-vacation-rental-software.html
            handles direct hits with canonical + meta refresh; this <Navigate> handles SPA nav. */}
        <Route path="/compare/best-vacation-rental-software" element={<LoggedNavigate to="/compare/best-holiday-rental-software" />} />

        {/* Legacy blog slugs (shared across all languages) → new "holiday-rentals" slugs. */}
        <Route path="/blog/maximize-occupancy-rates-vacation-rentals" element={<LoggedNavigate to="/blog/maximize-occupancy-rates-holiday-rentals" />} />
        <Route path="/blog/property-maintenance-vacation-rentals" element={<LoggedNavigate to="/blog/property-maintenance-holiday-rentals" />} />
        {(['nl','es','de','fr','it','pt','dk'] as const).map((lang) => (
          <Route
            key={`legacy-blog-occ-${lang}`}
            path={`/${lang}/blog/maximize-occupancy-rates-vacation-rentals`}
            element={<LoggedNavigate to={`/${lang}/blog/maximize-occupancy-rates-holiday-rentals`} />}
          />
        ))}
        {(['nl','es','de','fr','it','pt','dk'] as const).map((lang) => (
          <Route
            key={`legacy-blog-maint-${lang}`}
            path={`/${lang}/blog/property-maintenance-vacation-rentals`}
            element={<LoggedNavigate to={`/${lang}/blog/property-maintenance-holiday-rentals`} />}
          />
        ))}

        
        {/* ===== DUTCH (NL) ROUTES ===== */}
        <Route path="/nl" element={HomePage} />
        <Route path="/nl/prijzen" element={withPage(PricingPage)} />
        <Route path="/nl/vergelijken" element={withPage(ComparePage)} />
        <Route path="/nl/functies" element={withPage(FeaturesPage)} />
        <Route path="/nl/wat-is-irent" element={withPage(BriefDescriptionPage)} />
        <Route path="/nl/contact" element={withPage(ContactPage)} />
        <Route path="/nl/over-ons" element={withPage(AboutPage)} />
        <Route path="/nl/referenties" element={withPage(TestimonialsPage)} />
        <Route path="/nl/integraties" element={withPage(IntegrationsPage)} />
        <Route path="/nl/product/vakantieverhuur-software" element={withPage(PMSPage)} />
        <Route path="/nl/product/channel-manager-vakantieverhuur" element={withPage(ChannelManagerPage)} />
        <Route path="/nl/product/website-bouwer" element={withPage(WebsiteBuilderPage)} />
        <Route path="/nl/oplossingen/kleine-verhuurders" element={withPage(SmallBusinessPage)} />
        <Route path="/nl/oplossingen/grotere-verhuurders" element={withPage(MediumLargePage)} />
        <Route path="/nl/blog" element={withPage(BlogPage)} />
        <Route path="/nl/blog/:slug" element={withPage(BlogPostPage)} />
        <Route path="/nl/faq" element={withPage(FAQPage)} />
        <Route path="/nl/voorwaarden" element={withPage(TermsPage)} />
        <Route path="/nl/privacy" element={withPage(PrivacyPage)} />
        <Route path="/nl/publicatievoorwaarden" element={withPage(PublicationTermsPage)} />
        <Route path="/nl/website-voorwaarden" element={withPage(WebsiteDevelopmentTermsPage)} />
        <Route path="/nl/ai" element={withPage(AIPage)} />
        
        {/* ===== SPANISH (ES) ROUTES ===== */}
        <Route path="/es" element={HomePage} />
        <Route path="/es/precios" element={withPage(PricingPage)} />
        <Route path="/es/comparar" element={withPage(ComparePage)} />
        <Route path="/es/funcionalidades" element={withPage(FeaturesPage)} />
        <Route path="/es/que-es-irent" element={withPage(BriefDescriptionPage)} />
        <Route path="/es/contacto" element={withPage(ContactPage)} />
        <Route path="/es/nosotros" element={withPage(AboutPage)} />
        <Route path="/es/testimonios" element={withPage(TestimonialsPage)} />
        <Route path="/es/integraciones" element={withPage(IntegrationsPage)} />
        <Route path="/es/producto/software-alquiler-vacacional" element={withPage(PMSPage)} />
        <Route path="/es/producto/channel-manager-alquiler-vacacional" element={withPage(ChannelManagerPage)} />
        <Route path="/es/producto/creador-web" element={withPage(WebsiteBuilderPage)} />
        <Route path="/es/soluciones/pequenos-propietarios" element={withPage(SmallBusinessPage)} />
        <Route path="/es/soluciones/grandes-propietarios" element={withPage(MediumLargePage)} />
        <Route path="/es/blog" element={withPage(BlogPage)} />
        <Route path="/es/blog/:slug" element={withPage(BlogPostPage)} />
        <Route path="/es/faq" element={withPage(FAQPage)} />
        <Route path="/es/terminos" element={withPage(TermsPage)} />
        <Route path="/es/privacidad" element={withPage(PrivacyPage)} />
        <Route path="/es/terminos-publicacion" element={withPage(PublicationTermsPage)} />
        <Route path="/es/terminos-desarrollo-web" element={withPage(WebsiteDevelopmentTermsPage)} />
        <Route path="/es/ia" element={withPage(AIPage)} />
        
        {/* ===== GERMAN (DE) ROUTES ===== */}
        <Route path="/de" element={HomePage} />
        <Route path="/de/preise" element={withPage(PricingPage)} />
        <Route path="/de/vergleichen" element={withPage(ComparePage)} />
        <Route path="/de/funktionen" element={withPage(FeaturesPage)} />
        <Route path="/de/was-ist-irent" element={withPage(BriefDescriptionPage)} />
        <Route path="/de/kontakt" element={withPage(ContactPage)} />
        <Route path="/de/ueber-uns" element={withPage(AboutPage)} />
        <Route path="/de/referenzen" element={withPage(TestimonialsPage)} />
        <Route path="/de/integrationen" element={withPage(IntegrationsPage)} />
        <Route path="/de/produkt/ferienvermietung-software" element={withPage(PMSPage)} />
        <Route path="/de/produkt/channel-manager-ferienvermietung" element={withPage(ChannelManagerPage)} />
        <Route path="/de/produkt/website-baukasten" element={withPage(WebsiteBuilderPage)} />
        <Route path="/de/loesungen/kleine-vermieter" element={withPage(SmallBusinessPage)} />
        <Route path="/de/loesungen/grosse-vermieter" element={withPage(MediumLargePage)} />
        <Route path="/de/blog" element={withPage(BlogPage)} />
        <Route path="/de/blog/:slug" element={withPage(BlogPostPage)} />
        <Route path="/de/faq" element={withPage(FAQPage)} />
        <Route path="/de/agb" element={withPage(TermsPage)} />
        <Route path="/de/datenschutz" element={withPage(PrivacyPage)} />
        <Route path="/de/veroeffentlichungsbedingungen" element={withPage(PublicationTermsPage)} />
        <Route path="/de/website-entwicklungsbedingungen" element={withPage(WebsiteDevelopmentTermsPage)} />
        <Route path="/de/ki" element={withPage(AIPage)} />
        
        {/* ===== FRENCH (FR) ROUTES ===== */}
        <Route path="/fr" element={HomePage} />
        <Route path="/fr/tarifs" element={withPage(PricingPage)} />
        <Route path="/fr/comparer" element={withPage(ComparePage)} />
        <Route path="/fr/fonctionnalites" element={withPage(FeaturesPage)} />
        <Route path="/fr/quest-ce-que-irent" element={withPage(BriefDescriptionPage)} />
        <Route path="/fr/contact" element={withPage(ContactPage)} />
        <Route path="/fr/a-propos" element={withPage(AboutPage)} />
        <Route path="/fr/temoignages" element={withPage(TestimonialsPage)} />
        <Route path="/fr/integrations" element={withPage(IntegrationsPage)} />
        <Route path="/fr/produit/logiciel-location-vacances" element={withPage(PMSPage)} />
        <Route path="/fr/produit/channel-manager-location-vacances" element={withPage(ChannelManagerPage)} />
        <Route path="/fr/produit/createur-site" element={withPage(WebsiteBuilderPage)} />
        <Route path="/fr/solutions/petits-proprietaires" element={withPage(SmallBusinessPage)} />
        <Route path="/fr/solutions/grands-proprietaires" element={withPage(MediumLargePage)} />
        <Route path="/fr/blog" element={withPage(BlogPage)} />
        <Route path="/fr/blog/:slug" element={withPage(BlogPostPage)} />
        <Route path="/fr/faq" element={withPage(FAQPage)} />
        <Route path="/fr/conditions" element={withPage(TermsPage)} />
        <Route path="/fr/confidentialite" element={withPage(PrivacyPage)} />
        <Route path="/fr/conditions-publication" element={withPage(PublicationTermsPage)} />
        <Route path="/fr/conditions-developpement-web" element={withPage(WebsiteDevelopmentTermsPage)} />
        <Route path="/fr/ia" element={withPage(AIPage)} />
        
        {/* ===== ITALIAN (IT) ROUTES ===== */}
        <Route path="/it" element={HomePage} />
        <Route path="/it/prezzi" element={withPage(PricingPage)} />
        <Route path="/it/confronto" element={withPage(ComparePage)} />
        <Route path="/it/funzionalita" element={withPage(FeaturesPage)} />
        <Route path="/it/cose-irent" element={withPage(BriefDescriptionPage)} />
        <Route path="/it/contatti" element={withPage(ContactPage)} />
        <Route path="/it/chi-siamo" element={withPage(AboutPage)} />
        <Route path="/it/testimonianze" element={withPage(TestimonialsPage)} />
        <Route path="/it/integrazioni" element={withPage(IntegrationsPage)} />
        <Route path="/it/prodotto/software-affitti-brevi" element={withPage(PMSPage)} />
        <Route path="/it/prodotto/channel-manager-affitti-brevi" element={withPage(ChannelManagerPage)} />
        <Route path="/it/prodotto/costruttore-siti" element={withPage(WebsiteBuilderPage)} />
        <Route path="/it/soluzioni/piccoli-proprietari" element={withPage(SmallBusinessPage)} />
        <Route path="/it/soluzioni/grandi-proprietari" element={withPage(MediumLargePage)} />
        <Route path="/it/blog" element={withPage(BlogPage)} />
        <Route path="/it/blog/:slug" element={withPage(BlogPostPage)} />
        <Route path="/it/faq" element={withPage(FAQPage)} />
        <Route path="/it/termini" element={withPage(TermsPage)} />
        <Route path="/it/privacy" element={withPage(PrivacyPage)} />
        <Route path="/it/termini-pubblicazione" element={withPage(PublicationTermsPage)} />
        <Route path="/it/termini-sviluppo-web" element={withPage(WebsiteDevelopmentTermsPage)} />
        <Route path="/it/ia" element={withPage(AIPage)} />
        
        {/* ===== PORTUGUESE (PT) ROUTES ===== */}
        <Route path="/pt" element={HomePage} />
        <Route path="/pt/precos" element={withPage(PricingPage)} />
        <Route path="/pt/comparar" element={withPage(ComparePage)} />
        <Route path="/pt/funcionalidades" element={withPage(FeaturesPage)} />
        <Route path="/pt/o-que-e-irent" element={withPage(BriefDescriptionPage)} />
        <Route path="/pt/contacto" element={withPage(ContactPage)} />
        <Route path="/pt/sobre-nos" element={withPage(AboutPage)} />
        <Route path="/pt/testemunhos" element={withPage(TestimonialsPage)} />
        <Route path="/pt/integracoes" element={withPage(IntegrationsPage)} />
        <Route path="/pt/produto/software-alojamento-local" element={withPage(PMSPage)} />
        <Route path="/pt/produto/channel-manager-alojamento-local" element={withPage(ChannelManagerPage)} />
        <Route path="/pt/produto/criador-sites" element={withPage(WebsiteBuilderPage)} />
        <Route path="/pt/solucoes/pequenos-proprietarios" element={withPage(SmallBusinessPage)} />
        <Route path="/pt/solucoes/grandes-proprietarios" element={withPage(MediumLargePage)} />
        <Route path="/pt/blog" element={withPage(BlogPage)} />
        <Route path="/pt/blog/:slug" element={withPage(BlogPostPage)} />
        <Route path="/pt/faq" element={withPage(FAQPage)} />
        <Route path="/pt/termos" element={withPage(TermsPage)} />
        <Route path="/pt/privacidade" element={withPage(PrivacyPage)} />
        <Route path="/pt/termos-publicacao" element={withPage(PublicationTermsPage)} />
        <Route path="/pt/termos-desenvolvimento-web" element={withPage(WebsiteDevelopmentTermsPage)} />
        <Route path="/pt/ia" element={withPage(AIPage)} />
        
        {/* ===== DANISH (DK) ROUTES ===== */}
        <Route path="/dk" element={HomePage} />
        <Route path="/dk/priser" element={withPage(PricingPage)} />
        <Route path="/dk/sammenlign" element={withPage(ComparePage)} />
        <Route path="/dk/funktioner" element={withPage(FeaturesPage)} />
        <Route path="/dk/hvad-er-irent" element={withPage(BriefDescriptionPage)} />
        <Route path="/dk/kontakt" element={withPage(ContactPage)} />
        <Route path="/dk/om-os" element={withPage(AboutPage)} />
        <Route path="/dk/anmeldelser" element={withPage(TestimonialsPage)} />
        <Route path="/dk/integrationer" element={withPage(IntegrationsPage)} />
        <Route path="/dk/produkt/feriebolig-software" element={withPage(PMSPage)} />
        <Route path="/dk/produkt/channel-manager-feriebolig" element={withPage(ChannelManagerPage)} />
        <Route path="/dk/produkt/hjemmeside-bygger" element={withPage(WebsiteBuilderPage)} />
        <Route path="/dk/loesninger/smaa-udlejere" element={withPage(SmallBusinessPage)} />
        <Route path="/dk/loesninger/store-udlejere" element={withPage(MediumLargePage)} />
        <Route path="/dk/blog" element={withPage(BlogPage)} />
        <Route path="/dk/blog/:slug" element={withPage(BlogPostPage)} />
        <Route path="/dk/faq" element={withPage(FAQPage)} />
        <Route path="/dk/vilkaar" element={withPage(TermsPage)} />
        <Route path="/dk/privatlivspolitik" element={withPage(PrivacyPage)} />
        <Route path="/dk/publikationsvilkaar" element={withPage(PublicationTermsPage)} />
        <Route path="/dk/webstedsudviklingsvilkaar" element={withPage(WebsiteDevelopmentTermsPage)} />
        <Route path="/dk/ai" element={withPage(AIPage)} />
        
        {/* Auth & Admin */}
        <Route path="/login" element={withPage(AuthPage)} />
        <Route path="/reset-password" element={withPage(ResetPasswordPage)} />
        <Route path="/admin" element={withPage(AdminPage)} />
        <Route path="/admin/blog" element={withPage(BlogEditorPage)} />
        <Route path="/admin/blog/:id" element={withPage(BlogPostEditor)} />
        <Route path="/admin/testimonials" element={withPage(TestimonialsEditor)} />
        <Route path="/admin/users" element={withPage(UserManagementPage)} />
        <Route path="/admin/email-settings" element={withPage(EmailSettingsPage)} />
        <Route path="/admin/photo-album" element={withPage(PhotoAlbumPage)} />
        <Route path="/admin/seo" element={withPage(SeoMetaEditor)} />
        <Route path="/admin/dns-check" element={withPage(DnsCheckPage)} />
        <Route path="/admin/sitemap-check" element={withPage(SitemapCheckPage)} />
        <Route path="/admin/seo-publish-status" element={withPage(SeoPublishStatusPage)} />
        <Route path="/admin/url-monitor" element={withPage(UrlMonitorPage)} />
        <Route path="/admin/gsc-inspections" element={withPage(GscInspectionsPage)} />
        <Route path="/admin/gsc-monitor" element={withPage(GscMonitorPage)} />
        <Route path="/admin/seo-validation" element={withPage(SeoValidationPage)} />
        <Route path="/admin/redirect-check" element={withPage(RedirectCheckPage)} />
        <Route path="/admin/ux-metrics" element={withPage(UxMetricsPage)} />


        {/* Mobile App Privacy Policies (single shared URL per app, language switch in-page) */}
        <Route path="/app-owners-privacy-policy" element={withPage(AppOwnersPrivacyPage)} />
        <Route path="/app-maintenance-privacy-policy" element={withPage(AppMaintenancePrivacyPage)} />
        <Route path="/app-property-management-privacy-policy" element={withPage(AppPropertyManagerPrivacyPage)} />

        {/* Legacy PMS URL redirects (renamed 2026-07) */}
        <Route path="/product/pms" element={<LoggedNavigate to="/product/holiday-rental-software" replace />} />
        <Route path="/nl/product/pms" element={<LoggedNavigate to="/nl/product/vakantieverhuur-software" replace />} />
        <Route path="/es/producto/pms" element={<LoggedNavigate to="/es/producto/software-alquiler-vacacional" replace />} />
        <Route path="/de/produkt/pms" element={<LoggedNavigate to="/de/produkt/ferienvermietung-software" replace />} />
        <Route path="/fr/produit/pms" element={<LoggedNavigate to="/fr/produit/logiciel-location-vacances" replace />} />
        <Route path="/it/prodotto/pms" element={<LoggedNavigate to="/it/prodotto/software-affitti-brevi" replace />} />
        <Route path="/pt/produto/pms" element={<LoggedNavigate to="/pt/produto/software-alojamento-local" replace />} />
        <Route path="/dk/produkt/pms" element={<LoggedNavigate to="/dk/produkt/feriebolig-software" replace />} />

        {/* Legacy Channel Manager URL redirects (renamed 2026-07) */}
        <Route path="/product/channel-manager" element={<LoggedNavigate to="/product/channel-manager-holiday-rentals" replace />} />
        <Route path="/nl/product/channel-manager" element={<LoggedNavigate to="/nl/product/channel-manager-vakantieverhuur" replace />} />
        <Route path="/es/producto/channel-manager" element={<LoggedNavigate to="/es/producto/channel-manager-alquiler-vacacional" replace />} />
        <Route path="/de/produkt/channel-manager" element={<LoggedNavigate to="/de/produkt/channel-manager-ferienvermietung" replace />} />
        <Route path="/fr/produit/channel-manager" element={<LoggedNavigate to="/fr/produit/channel-manager-location-vacances" replace />} />
        <Route path="/it/prodotto/channel-manager" element={<LoggedNavigate to="/it/prodotto/channel-manager-affitti-brevi" replace />} />
        <Route path="/pt/produto/channel-manager" element={<LoggedNavigate to="/pt/produto/channel-manager-alojamento-local" replace />} />
        <Route path="/dk/produkt/channel-manager" element={<LoggedNavigate to="/dk/produkt/channel-manager-feriebolig" replace />} />

        {/* Catch-all */}
        <Route path="*" element={withPage(NotFound)} />

      </Routes>
    </AnimatePresence>
  );
}
